<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query';
import { UiButton, UiText, UiDialog, UiCard, UiIcon, type ProjectReviewStatus, useApi, useToaster } from '@shopexperts/shared';
import { useAuthStore } from '@/store/auth';
import ReviewForm, { type ReviewFormValues } from '@/app/components/reviews/ReviewForm.vue';
import type { ExpertData } from '@/app/components/reviews/ExpertDetailsCard.vue';
import type { ProjectDetail } from '@/v2Projects/types';

const props = defineProps<{ project: ProjectDetail }>();
const emit = defineEmits<{ status: [value: ProjectReviewStatus | undefined] }>();
const api = useApi();
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const toaster = useToaster();
const isOpen = ref(false);
const submissionError = ref('');
const queryKey = computed(() => ['v2projects', 'review', auth.user?.id, props.project.id]);
const { data: status, error, isFetching, refetch } = useQuery({
  queryKey,
  queryFn: () => api.client.projectsV2.getReview({ params: { projectId: props.project.id } }),
  staleTime: 0,
});
watch(status, value => emit('status', value), { immediate: true });
const expert = computed<ExpertData>(() => ({
  name: props.project.expert?.name || 'Your expert',
  type: props.project.expert?.type === 'freelancer' ? 'freelancer' : 'agency',
  slug: '',
  profile_picture: props.project.expert?.avatar,
  role: props.project.expert?.role,
}));

// Only a transition observed on this project opens automatically. Reloading a
// completed project keeps the banner available without repeatedly opening it.
watch(() => props.project.project_status, async (next, previous) => {
  if (next === previous) return;
  const result = await refetch();
  if (next === 'completed' && !result.error && result.data?.can_submit_review && result.data.auto_prompt_review) isOpen.value = true;
});

watch([status, isFetching, () => route.query.review], async ([current, fetching, requested]) => {
  if (requested !== '1' || fetching || !current || error.value) return;
  isOpen.value = true;
  const { review: _review, ...query } = route.query;
  await router.replace({ query });
}, { immediate: true });

const submit = useMutation({
  onMutate: () => ({ queryKey: [...queryKey.value] }),
  mutationFn: (values: ReviewFormValues) => api.client.projectsV2.submitReview({
    params: { projectId: props.project.id },
    body: {
      quality: values.quality,
      communication: values.communication,
      timelines: values.timelines,
      value_for_money: values.value_for_money,
      review_text: values.review_text,
      is_anonymous: values.is_anonymous,
    },
  }),
  onSuccess: async (result, _values, context) => {
    queryClient.setQueryData(context.queryKey, result);
    isOpen.value = false;
    submissionError.value = '';
    toaster.create({ title: 'Review submitted', type: 'success' });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['v2projects'] }),
      queryClient.invalidateQueries({ queryKey: ['client', 'reviews'] }),
      queryClient.invalidateQueries({ queryKey: ['client', 'review-requests'] }),
      queryClient.invalidateQueries({ queryKey: ['expert', 'review-requests'] }),
      queryClient.invalidateQueries({ queryKey: ['expert', 'reviews'] }),
      queryClient.invalidateQueries({ queryKey: ['expert', 'review-projects'] }),
    ]);
  },
  onError: async (failure: Error) => {
    submissionError.value = failure.message || 'Could not submit your review. Please try again.';
    // A concurrent submission can make this form ineligible. Refresh the status
    // before allowing another attempt; keep the draft for recoverable errors.
    await refetch();
  },
});
function handleSubmit(values: ReviewFormValues) {
  if (!status.value?.can_submit_review || submit.isPending.value) return;
  submissionError.value = '';
  submit.mutate(values);
}
</script>

<template>
  <div v-if="error || status?.show_review_banner" class="px-5 pt-4">
    <UiCard :class="error ? '' : '!bg-se-success-light !border-se-primary'">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <template v-if="error">
          <UiText as="p" variant="p-small" text="Could not load the project review." />
          <UiButton variant="outline" label="Retry" :disabled="isFetching" @click="refetch()" />
        </template>
        <template v-else>
          <div class="flex items-start gap-3">
            <div class="rounded-full bg-se-primary p-2 shrink-0"><UiIcon name="star" class="size-5 text-se-dark" /></div>
            <div class="flex flex-col gap-1">
              <UiText as="p" variant="h6" text="All done! How did it go?" />
              <UiText as="p" variant="p-small" class="text-se-dark" :text="`We’d love to hear about your experience with ${expert.name}. Your review helps them grow and other clients find their perfect match.`" />
            </div>
          </div>
          <UiButton class="shrink-0" label="Leave a review" @click="isOpen = true" />
        </template>
      </div>
    </UiCard>
  </div>
  <UiDialog v-model="isOpen" size="xl" :title="status?.has_review ? 'Project review' : 'Leave a review'" :hide-close-button="submit.isPending.value">
    <template #default>
      <div v-if="status?.review" class="flex flex-col gap-4">
        <UiText as="p" variant="h6" :text="project.title" />
        <UiText as="p" variant="p" :text="`${status.review.overall_rating} / 5 · ${status.review.status}`" />
        <div class="grid grid-cols-2 gap-2">
          <UiText as="p" variant="p-small" :text="`Quality: ${status.review.quality} / 5`" />
          <UiText as="p" variant="p-small" :text="`Communication: ${status.review.communication} / 5`" />
          <UiText as="p" variant="p-small" :text="`Timelines: ${status.review.timelines} / 5`" />
          <UiText as="p" variant="p-small" :text="`Value for money: ${status.review.value_for_money} / 5`" />
        </div>
        <UiText as="p" variant="p" class="whitespace-pre-wrap" :text="status.review.review_text" />
      </div>
      <template v-else-if="status?.can_submit_review">
        <UiText as="p" v-if="submissionError" variant="p-small" class="text-se-danger" :text="submissionError" />
        <ReviewForm v-if="isOpen" :expert="expert" :show-close-button="false" :is-submitting="submit.isPending.value"
          :review-text-max-length="5000" :description="`Share your experience on ${project.title}.`"
          @submit="handleSubmit" @cancel="isOpen = false" />
      </template>
      <UiText as="p" v-else variant="p" :text="status?.has_review ? 'A review has already been submitted for this project.' : 'Reviews are available to clients after the project is completed.'" />
    </template>
  </UiDialog>
</template>
